import {
  redirect,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import { invokeFunction } from "@shared/functions.ts";
import {
  formatSupabaseFunctionError,
  isSupabaseResponseError,
} from "@shared/functions/supabaseFunctionError.ts";
import GenerateDerivativeDatasetContract from "@shared/functions/generate-derivative-dataset.ts";
import StartSpecificationRunContract from "@shared/functions/start-specification-run.ts";
import type { Database } from "@/integrations/supabase/types";

export const SPECIFICATIONS_LOADER_ID = "specificationsLoader";

type SpecificationRow =
  Database["public"]["Tables"]["specifications"]["Row"];

type SpecificationDatasetRow =
  Database["public"]["Tables"]["specification_datasets"]["Row"];

type TestDataSetRow = Database["public"]["Tables"]["test_data_sets"]["Row"];
type SpecificationRunStatusRow = Pick<
  Database["public"]["Views"]["pgflow_specification_run_statuses"]["Row"],
  | "specification_run_id"
  | "specification_id"
  | "specification_run_status"
  | "specification_run_started_at"
  | "specification_run_completed_at"
  | "latest_step_slug"
  | "latest_step_status"
  | "latest_step_number"
  | "total_steps"
  | "completed_steps"
  | "last_error_message"
>;

export type SpecificationRunSummary = {
  id: string;
  specification_id: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  latest_step_slug: string | null;
  latest_step_status: string | null;
  latest_step_number: number | null;
  total_steps: number | null;
  completed_steps: number | null;
  last_error_message: string | null;
};

type SpecificationDatasetWithDataset = SpecificationDatasetRow & {
  test_data_sets?: Pick<
    TestDataSetRow,
    "id" | "name" | "dataset_kind" | "creation_method" | "type"
  > | null;
};

type SpecificationWithDatasets = SpecificationRow & {
  specification_datasets?: SpecificationDatasetWithDataset[] | null;
  latest_run?: SpecificationRunSummary | null;
};

export type SpecificationsLoaderData = {
  projectId: string;
  projectTitle: string;
  specifications: SpecificationWithDatasets[];
};

export type SpecificationsActionResult =
  | {
      ok: true;
      intent: "specificationCreate";
      specificationId: string;
    }
  | {
      ok: true;
      intent: "specificationRun";
      specificationId: string;
      pgflowRunId: string;
    }
  | {
      ok: true;
      intent: "specificationDelete";
      specificationId: string;
    }
  | { ok: false; intent?: string; error: string };

const ensureSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session?.user) {
    throw redirect("/auth");
  }

  return session;
};

const ensureUser = async () => (await ensureSession()).user;

const getRequiredValue = (formData: FormData, key: string) => {
  const value = formData.get(key);
  if (typeof value !== "string" || !value.trim()) {
    throw new Response(`${key} is required`, { status: 400 });
  }
  return value.trim();
};

type TestRunEvalType = "Strict Equality" | "Any permitted" | "LLM-As-A-Judge";

const getEvalType = (value: string): TestRunEvalType => {
  const supported: TestRunEvalType[] = [
    "Strict Equality",
    "Any permitted",
    "LLM-As-A-Judge",
  ];
  if ((supported as string[]).includes(value)) {
    return value as TestRunEvalType;
  }
  throw new Response("Unsupported evaluation type", { status: 400 });
};

const formatAttackMethodLabel = (value: string): string =>
  value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const specificationsLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<SpecificationsLoaderData> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw redirect("/auth");
  }

  const projectId = params["projectId"] || params["evalId"];
  if (!projectId) {
    throw new Response("Project ID required", { status: 400 });
  }

  const { data: project, error: projectError } = await supabase
    .from("projects")
    .select("id, title")
    .eq("id", projectId)
    .single();

  if (projectError) {
    throw projectError;
  }
  if (!project) {
    throw new Response("Project not found", { status: 404 });
  }

  const { data: specifications, error: specificationsError } = await supabase
    .from("specifications")
    .select(
      `*, specification_datasets(*, test_data_sets!specification_datasets_dataset_id_fkey(id, name, dataset_kind, creation_method, type))`,
    )
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });

  if (specificationsError) {
    throw specificationsError;
  }

  const { data: specRuns, error: specRunsError } = await supabase
    .from("pgflow_specification_run_statuses")
    .select(
      "specification_run_id, specification_id, specification_run_status, specification_run_started_at, specification_run_completed_at, latest_step_slug, latest_step_status, latest_step_number, total_steps, completed_steps, last_error_message",
    )
    .eq("project_id", projectId)
    .order("specification_run_started_at", { ascending: false });

  if (specRunsError) {
    throw specRunsError;
  }

  const latestRunBySpec = new Map<string, SpecificationRunSummary>();
  (specRuns ?? []).forEach((run: SpecificationRunStatusRow) => {
    if (
      !run.specification_run_id ||
      !run.specification_id ||
      !run.specification_run_status ||
      !run.specification_run_started_at
    ) {
      return;
    }

    if (!latestRunBySpec.has(run.specification_id)) {
      latestRunBySpec.set(run.specification_id, {
        id: run.specification_run_id,
        specification_id: run.specification_id,
        status: run.specification_run_status,
        started_at: run.specification_run_started_at,
        completed_at: run.specification_run_completed_at,
        latest_step_slug: run.latest_step_slug,
        latest_step_status: run.latest_step_status,
        latest_step_number: run.latest_step_number,
        total_steps: run.total_steps,
        completed_steps: run.completed_steps,
        last_error_message: run.last_error_message,
      });
    }
  });

  const specificationsWithRuns = (specifications ?? []).map((spec) => ({
    ...(spec as SpecificationWithDatasets),
    latest_run: latestRunBySpec.get(spec.id) ?? null,
  })) as SpecificationWithDatasets[];

  return {
    projectId: project.id,
    projectTitle: project.title,
    specifications: specificationsWithRuns,
  };
};

export const specificationsAction = async ({
  request,
}: ActionFunctionArgs): Promise<SpecificationsActionResult> => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const intentValue = typeof intent === "string" ? intent : undefined;

  if (
    intent !== "specificationCreate" &&
    intent !== "specificationRun" &&
    intent !== "specificationDelete"
  ) {
    return { ok: false, error: "Unsupported specification action" };
  }

  if (intent === "specificationDelete") {
    await ensureUser();
    const safeIntent = "specificationDelete" as const;
    const specificationId = getRequiredValue(formData, "specificationId");

    const { data: spec, error: fetchError } = await supabase
      .from("specifications")
      .select("id, locked_at")
      .eq("id", specificationId)
      .single();

    if (fetchError || !spec) {
      return {
        ok: false,
        intent: safeIntent,
        error: fetchError?.message ?? "Specification not found",
      };
    }

    if (spec.locked_at) {
      return {
        ok: false,
        intent: safeIntent,
        error: "Cannot delete a locked specification",
      };
    }

    const { error: deleteError } = await supabase
      .from("specifications")
      .delete()
      .eq("id", specificationId);

    if (deleteError) {
      return {
        ok: false,
        intent: safeIntent,
        error: deleteError.message,
      };
    }

    return {
      ok: true,
      intent: safeIntent,
      specificationId,
    };
  }

  try {
    if (intent === "specificationCreate") {
      const user = await ensureUser();
      const safeIntent = "specificationCreate" as const;
      const projectId = getRequiredValue(formData, "projectId");
      const name = getRequiredValue(formData, "name");
      const primaryDatasetId = getRequiredValue(formData, "primaryDatasetId");
      const evalType = getEvalType(getRequiredValue(formData, "evalType"));
      const descriptionValue = formData.get("description");
      const description =
        typeof descriptionValue === "string" && descriptionValue.trim().length > 0
          ? descriptionValue.trim()
          : null;
      const permittedValuesRaw = formData.get("permittedValues");
      const judgeIdRaw = formData.get("judgeId");

      if (evalType === "Any permitted") {
        if (
          typeof permittedValuesRaw !== "string" ||
          !permittedValuesRaw.trim()
        ) {
          return {
            ok: false,
            intent: safeIntent,
            error:
              'Permitted values are required for "Any permitted" evaluation',
          };
        }
      }

      if (evalType === "LLM-As-A-Judge") {
        if (typeof judgeIdRaw !== "string" || !judgeIdRaw.trim()) {
          return {
            ok: false,
            intent: safeIntent,
            error:
              "Judge selection is required for LLM-as-a-Judge evaluation",
          };
        }
      }

      const attackMethodsRaw = formData.get("attackMethods");
      let attackMethods: string[] = [];
      if (typeof attackMethodsRaw === "string" && attackMethodsRaw.trim()) {
        try {
          const parsed = JSON.parse(attackMethodsRaw);
          if (Array.isArray(parsed)) {
            attackMethods = parsed
              .filter((value) => typeof value === "string")
              .map((value) => value.trim())
              .filter(Boolean);
          }
        } catch {
          attackMethods = [];
        }
      }

      if (attackMethods.length === 0) {
        return {
          ok: false,
          intent: safeIntent,
          error: "Select at least one attack method",
        };
      }

      const { data: primaryDataset, error: datasetError } = await supabase
        .from("test_data_sets")
        .select("id, name, dataset_kind")
        .eq("id", primaryDatasetId)
        .single();

      if (datasetError || !primaryDataset) {
        return {
          ok: false,
          intent: safeIntent,
          error: datasetError?.message ?? "Primary dataset not found",
        };
      }

      const permittedValues =
        evalType === "Any permitted" && typeof permittedValuesRaw === "string"
          ? permittedValuesRaw.trim()
          : null;
      const judgeId =
        evalType === "LLM-As-A-Judge" && typeof judgeIdRaw === "string"
          ? judgeIdRaw.trim()
          : null;

      const { data: specification, error: specificationError } = await supabase
        .from("specifications")
        .insert({
          project_id: projectId,
          name,
          description,
          eval_type: evalType,
          judge_id: judgeId,
          permitted_values: permittedValues,
          created_by: user.id,
        })
        .select("id")
        .single();

      if (specificationError || !specification) {
        return {
          ok: false,
          intent: safeIntent,
          error:
            specificationError?.message ?? "Failed to create specification",
        };
      }

      const derivativeRows = attackMethods.map((method) => ({
        specification_id: specification.id,
        role: "adversarial" as const,
        parent_dataset_id: primaryDataset.id,
        attack_method: method,
        status: "generating" as const,
      }));

      const { data: specDatasets, error: specDatasetError } = await supabase
        .from("specification_datasets")
        .insert([
          {
            specification_id: specification.id,
            role: "primary",
            dataset_id: primaryDataset.id,
            status: "ready",
          },
          ...derivativeRows,
        ])
        .select("id, role, attack_method");

      if (specDatasetError || !specDatasets) {
        await supabase
          .from("specifications")
          .delete()
          .eq("id", specification.id);
        return {
          ok: false,
          intent: safeIntent,
          error:
            specDatasetError?.message ??
            "Failed to create specification datasets",
        };
      }

      const derivativeSpecRows = specDatasets.filter(
        (row) => row.role === "adversarial",
      );

      await Promise.allSettled(
        derivativeSpecRows.map(async (row) => {
          const attackMethod = row.attack_method ?? "";
          if (!attackMethod) {
            await supabase
              .from("specification_datasets")
              .update({
                status: "failed",
                error: "Missing attack method",
              })
              .eq("id", row.id);
            return;
          }

          const datasetName = `${primaryDataset.name} - ${formatAttackMethodLabel(attackMethod)}`;

          try {
            const result = await invokeFunction(
              GenerateDerivativeDatasetContract,
              {
                client: supabase,
                body: {
                  parentDatasetId: primaryDataset.id,
                  newDatasetName: datasetName,
                  attackMethod,
                  userId: user.id,
                },
              },
            );

            if (!result.ok) {
              throw new Error(result.error.message);
            }

            const payload = result.data;
            if ("error" in payload) {
              throw new Error(
                payload.error ?? "Failed to generate adversarial dataset",
              );
            }

            await supabase
              .from("specification_datasets")
              .update({
                status: "ready",
                dataset_id: payload.datasetId,
                error: null,
              })
              .eq("id", row.id);
          } catch (error) {
            const message =
              error instanceof Error
                ? error.message
                : "Failed to generate adversarial dataset";
            await supabase
              .from("specification_datasets")
              .update({
                status: "failed",
                error: message,
              })
              .eq("id", row.id);
          }
        }),
      );

      return {
        ok: true,
        intent: "specificationCreate",
        specificationId: specification.id,
      };
    }

    const safeIntent = "specificationRun" as const;
    const session = await ensureSession();
    const specificationId = getRequiredValue(formData, "specificationId");

    const result = await invokeFunction(StartSpecificationRunContract, {
      client: supabase,
      body: { specificationId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!result.ok) {
      const status = isSupabaseResponseError(result.error)
        ? (result.error.status ?? 500)
        : 500;
      throw new Response(formatSupabaseFunctionError(result.error), {
        status,
      });
    }

    return {
      ok: true,
      intent: safeIntent,
      specificationId,
      pgflowRunId: result.data.pgflowRunId,
    };
  } catch (error) {
    if (error instanceof Response) {
      const errorMessage = await error.text();
      return intentValue
        ? { ok: false, intent: intentValue, error: errorMessage }
        : { ok: false, error: errorMessage };
    }

    const message = error instanceof Error ? error.message : "Unknown error";
    return intentValue
      ? { ok: false, intent: intentValue, error: message }
      : { ok: false, error: message };
  }
};
